import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Row,
  Col,
  Spin,
  Switch,
  Card,
  Alert,
  Typography,
  Tooltip,
  Divider,
} from "antd";
import { useHistory, useParams } from "react-router-dom";
import {
  findUserEmailById,
  updateUserEmailById,
  getActiveUserEmails,
} from "@api/email/users/userEmail";
import { getAllSmtp } from "@api/email/users/smtp";
import { getAllDomains } from "@api/email/users/domain";
import { EmailNotificationModal } from "@components";
import {
  LinkOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import "@assets/css/email-user-form.css";

const { Option } = Select;
const { Text } = Typography;

const UserEmailUpdateForm = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [smtpServers, setSmtpServers] = useState([]);
  const [filteredSmtpServers, setFilteredSmtpServers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [domainSmtpMap, setDomainSmtpMap] = useState({});
  const [smtpDomainMap, setSmtpDomainMap] = useState({});
  const [selectedDomain, setSelectedDomain] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [selectedSmtp, setSelectedSmtp] = useState(null);
  const [isExternal, setIsExternal] = useState(false);
  const [selectionMethod, setSelectionMethod] = useState("domain");
  const [autoSelectedField, setAutoSelectedField] = useState(null);

  // Shared inbox
  const [isSharedInbox, setIsSharedInbox] = useState(false);
  const [primaryAccounts, setPrimaryAccounts] = useState([]);
  const [originalPrimaryAccount, setOriginalPrimaryAccount] = useState(null);

  // Notification modal states
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, [id, form]);

  // After fetching both SMTP servers and domains, build relationship maps
  useEffect(() => {
    if (smtpServers.length > 0 && domains.length > 0) {
      buildRelationshipMaps();
    }
  }, [smtpServers, domains]);

  // Effect to highlight auto-selected field briefly
  useEffect(() => {
    if (autoSelectedField) {
      // Clear the auto-selected field after animation time
      const timeout = setTimeout(() => {
        setAutoSelectedField(null);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [autoSelectedField]);

  // Build maps of relationships between domains and SMTP servers
  const buildRelationshipMaps = () => {
    const domainToSmtp = {};
    const smtpToDomain = {};

    // Create domain to SMTP mapping
    domains.forEach((domain) => {
      const linkedSmtps = smtpServers.filter(
        (smtp) => smtp.domain === domain._id || smtp.domain?._id === domain._id
      );
      domainToSmtp[domain._id] = linkedSmtps;
    });

    // Create SMTP to domain mapping
    smtpServers.forEach((smtp) => {
      if (smtp.domain) {
        const domainId =
          typeof smtp.domain === "object" ? smtp.domain._id : smtp.domain;
        smtpToDomain[smtp._id] = domainId;
      }
    });

    setDomainSmtpMap(domainToSmtp);
    setSmtpDomainMap(smtpToDomain);
  };

  // Modal handling functions
  const showSuccessModal = (message) => {
    setSuccessModalVisible(true);
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  const showWarningModal = (message) => {
    setWarningMessage(message);
    setWarningModalVisible(true);
  };

  const showInfoModal = (message) => {
    setInfoMessage(message);
    setInfoModalVisible(true);
  };

  const fetchData = async () => {
    try {
      const [
        userResponse,
        smtpResponse,
        domainsResponse,
        primaryAccountsResponse,
      ] = await Promise.all([
        findUserEmailById(id),
        getAllSmtp(),
        getAllDomains(),
        getActiveUserEmails(),
      ]);

      const userData = userResponse.data;
      const activeServers = smtpResponse.data.filter(
        (server) => server.status === "active"
      );
      const activeDomains = domainsResponse.data.filter(
        (domain) => domain.status === "active"
      );

      // Filter out current email from primary accounts list
      const filteredPrimaryAccounts = primaryAccountsResponse.data.filter(
        (account) => account._id !== id
      );

      setSmtpServers(activeServers);
      setDomains(activeDomains);
      setPrimaryAccounts(filteredPrimaryAccounts);

      // Set shared inbox state
      setIsSharedInbox(userData.isSharedInbox || false);
      if (userData.sharedInboxConfig) {
        setOriginalPrimaryAccount(userData.sharedInboxConfig.primaryAccount);
      }

      // Find and set SMTP server
      const smtp = activeServers.find(
        (s) => s._id === (userData.smtp?._id || userData.smtp)
      );
      setSelectedSmtp(smtp);
      setIsExternal(smtp?.serverType === "other");

      if (smtp?.serverType === "mailinabox") {
        const [extractedUsername, domainPart] = userData.email.split("@");
        setUsername(extractedUsername);

        const userDomain = activeDomains.find(
          (d) => d.domainName === domainPart
        );
        if (userDomain) {
          setSelectedDomain(userDomain.domainName);
          const filtered = activeServers.filter(
            (smtp) => smtp.domain === userDomain._id
          );
          setFilteredSmtpServers(filtered);
        }
      }

      // Set form values
      form.setFieldsValue({
        domain: userData.domain?._id || userData.domain,
        email: userData.email,
        password: "",
        name: userData.name,
        smtp: userData.smtp?._id || userData.smtp,
        status: userData.status,
        quotaLimit: userData.quotaLimit,
        isAdmin: Boolean(userData.isAdmin),
        isSharedInbox: userData.isSharedInbox || false,
        sharedInboxName: userData.sharedInboxConfig?.name || userData.name,
        sharedInboxDescription: userData.sharedInboxConfig?.description || "",
        primaryAccount:
          userData.sharedInboxConfig?.primaryAccount?._id ||
          userData.sharedInboxConfig?.primaryAccount,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      showErrorModal("Failed to fetch user details. Please try again.");
    } finally {
      setInitializing(false);
    }
  };

  // Handler for shared inbox toggle
  const handleSharedInboxToggle = (checked) => {
    setIsSharedInbox(checked);
    if (checked) {
      form.setFieldsValue({
        isAdmin: true,
      });
    }
  };

  const validateUsername = (value) => {
    if (!value) {
      setUsernameError("Username is required");
      return false;
    }
    if (value.includes("@")) {
      setUsernameError("Username cannot contain @ symbol");
      return false;
    }
    if (value.includes(" ")) {
      setUsernameError("Username cannot contain spaces");
      return false;
    }
    setUsernameError("");
    return true;
  };

  const handleDomainChange = (domainId) => {
    setSelectionMethod("domain");
    const selectedDomainObj = domains.find((domain) => domain._id === domainId);

    if (selectedDomainObj) {
      setSelectedDomain(selectedDomainObj.domainName);

      // Get linked SMTP servers for this domain
      const linkedSmtps = domainSmtpMap[domainId] || [];
      setFilteredSmtpServers(linkedSmtps);

      // If there's exactly one SMTP server linked, auto-select it
      if (linkedSmtps.length === 1) {
        form.setFieldsValue({ smtp: linkedSmtps[0]._id });
        handleSmtpChange(linkedSmtps[0]._id);
        setAutoSelectedField("smtp");
        showInfoModal(
          `Automatically selected SMTP server: ${linkedSmtps[0].name}`
        );
      } else {
        // Clear SMTP selection if there are multiple options
        form.setFieldsValue({ smtp: undefined });
      }

      // Update email if username exists
      if (username) {
        const email = `${username}@${selectedDomainObj.domainName}`;
        form.setFieldsValue({ email });
      }
    }
  };

  const handleSmtpChange = (smtpId) => {
    const smtp = smtpServers.find((s) => s._id === smtpId);
    setSelectedSmtp(smtp);

    const newIsExternal = smtp?.serverType === "other";
    setIsExternal(newIsExternal);

    // If SMTP was selected first (not as a result of domain selection)
    if (selectionMethod !== "domain") {
      setSelectionMethod("smtp");
      // Check if this SMTP server is linked to a domain
      const linkedDomainId = smtpDomainMap[smtpId];

      if (linkedDomainId) {
        // Auto-select the linked domain
        form.setFieldsValue({ domain: linkedDomainId });
        const linkedDomain = domains.find((d) => d._id === linkedDomainId);
        if (linkedDomain) {
          setSelectedDomain(linkedDomain.domainName);
          setAutoSelectedField("domain");

          // Update email if username exists
          if (username) {
            const email = `${username}@${linkedDomain.domainName}`;
            form.setFieldsValue({ email });
          }

          showInfoModal(
            `Automatically selected domain: ${linkedDomain.domainName}`
          );
        }
      }
    }

    if (newIsExternal) {
      form.setFieldsValue({ domain: undefined, email: "" });
      setSelectedDomain("");
      setUsername("");
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/[^a-zA-Z0-9._-]/g, "");
    setUsername(sanitizedValue);

    if (validateUsername(sanitizedValue)) {
      if (selectedDomain) {
        const email = `${sanitizedValue}@${selectedDomain}`;
        form.setFieldsValue({ email });
      }
    } else {
      form.setFieldsValue({ email: "" });
    }
  };

  const onFinish = async (values) => {
    if (isExternal) {
      if (!values.email || !values.email.includes("@")) {
        showErrorModal("Please enter a valid email address");
        return;
      }
    } else {
      if (!validateUsername(username)) {
        return;
      }
      if (!selectedDomain) {
        showErrorModal("Please select a domain");
        return;
      }
    }

    // Validate shared inbox configuration
    if (isSharedInbox) {
      if (!values.primaryAccount) {
        showWarningModal(
          "Please select a primary account for the shared inbox"
        );
        return;
      }
    }

    setLoading(true);
    try {
      const updateData = {
        smtp: values.smtp,
        email: values.email,
        name: values.name,
        status: values.status,
        isAdmin: values.isAdmin,
        quotaLimit: values.quotaLimit,
        isSharedInbox: isSharedInbox,
      };

      // Only include password if it was changed
      if (values.password) {
        updateData.password = values.password;
      }

      // Add shared inbox specific data if needed
      if (isSharedInbox) {
        updateData.sharedInboxName = values.sharedInboxName;
        updateData.sharedInboxDescription = values.sharedInboxDescription;
        updateData.primaryAccount = values.primaryAccount;
      }

      await updateUserEmailById(id, updateData);
      setLoading(false);
      showSuccessModal("Email user updated successfully");

      // Give time for the modal to be visible before redirecting
      setTimeout(() => {
        history.goBack();
      }, 1500);
    } catch (error) {
      console.error("Error updating email user:", error);
      setLoading(false);
      showErrorModal(
        error.response?.data?.message || "Failed to update email user"
      );
    }
  };

  // Check if there are linked SMTP servers for the selected domain
  const hasLinkedSmtpServers = selectedDomain && filteredSmtpServers.length > 0;

  if (initializing) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="email-form-container">
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Shared Inbox Toggle */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Shared Inbox"
              name="isSharedInbox"
              valuePropName="checked"
              className="shared-inbox-toggle"
            >
              <Switch onChange={handleSharedInboxToggle} />
            </Form.Item>
          </Col>
        </Row>

        {/* Shared Inbox Configuration */}
        {isSharedInbox && (
          <Card
            title="Shared Inbox Configuration"
            className="shared-inbox-card"
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Shared Inbox Name"
                  name="sharedInboxName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter shared inbox name",
                    },
                  ]}
                >
                  <Input placeholder="Enter shared inbox name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Description" name="sharedInboxDescription">
                  <Input.TextArea placeholder="Enter description" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Primary Account"
                  name="primaryAccount"
                  rules={[
                    {
                      required: true,
                      message: "Please select primary account",
                    },
                  ]}
                  tooltip="Account with full access permissions"
                >
                  <Select placeholder="Select primary account">
                    {originalPrimaryAccount && (
                      <Option
                        key={originalPrimaryAccount._id}
                        value={originalPrimaryAccount._id}
                      >
                        {originalPrimaryAccount.email} (Current)
                      </Option>
                    )}
                    {primaryAccounts.map((account) => (
                      <Option key={account._id} value={account._id}>
                        {account.email} ({account.name})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Smart Selection Section */}
        <Card
          title="Email Configuration"
          className="email-config-card"
          style={{ marginBottom: 24 }}
        >
          <Row className="selection-info-row">
            <Col span={24}>
              <Alert
                message="Smart Selection"
                description="You can change either the domain or SMTP server. The system will automatically suggest related options when available."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            {/* Domain Selection */}
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Domain
                    <Tooltip title="Select a domain to see linked SMTP servers">
                      <InfoCircleOutlined style={{ marginLeft: 8 }} />
                    </Tooltip>
                  </span>
                }
                name="domain"
                required={!isExternal}
                validateStatus={!selectedDomain && !isExternal ? "error" : ""}
                help={
                  !selectedDomain && !isExternal ? "Please select a domain" : ""
                }
                className={
                  autoSelectedField === "domain" ? "auto-selected" : ""
                }
              >
                <Select
                  placeholder="Select domain"
                  onChange={handleDomainChange}
                  disabled={isExternal}
                  className="domain-select"
                >
                  {domains.map((domain) => (
                    <Option key={domain._id} value={domain._id}>
                      {domain.domainName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* SMTP Server Selection */}
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    SMTP Server
                    {selectedDomain && (
                      <span className="server-count-badge">
                        <LinkOutlined />{" "}
                        {filteredSmtpServers.length === 0
                          ? "No linked servers"
                          : `${filteredSmtpServers.length} linked server${
                              filteredSmtpServers.length !== 1 ? "s" : ""
                            }`}
                      </span>
                    )}
                  </span>
                }
                name="smtp"
                rules={[
                  { required: true, message: "Please select SMTP server" },
                ]}
                extra={
                  selectionMethod === "domain" &&
                  filteredSmtpServers.length === 0 ? (
                    <span className="relationship-hint">
                      No SMTP servers available for selected domain
                    </span>
                  ) : (
                    ""
                  )
                }
                className={autoSelectedField === "smtp" ? "auto-selected" : ""}
              >
                <Select
                  placeholder="Select SMTP server"
                  onChange={handleSmtpChange}
                  className="smtp-select"
                  notFoundContent={
                    selectionMethod === "domain" &&
                    filteredSmtpServers.length === 0
                      ? "No linked SMTP servers found"
                      : "No SMTP servers"
                  }
                >
                  {/* Show appropriate SMTP server options based on domain selection */}
                  {(selectionMethod === "domain"
                    ? filteredSmtpServers
                    : smtpServers
                  ).map((server) => (
                    <Option key={server._id} value={server._id}>
                      {server.name} ({server.provider})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {!isExternal ? (
            // Mail-in-a-Box Email Creation
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Username"
                    required
                    validateStatus={usernameError ? "error" : ""}
                    help={usernameError}
                    tooltip="Enter username (only letters, numbers, dots, hyphens, and underscores allowed)"
                  >
                    <Input
                      placeholder="Enter username"
                      onChange={handleUsernameChange}
                      value={username}
                      onBlur={() => validateUsername(username)}
                      maxLength={64}
                      addonAfter={
                        selectedDomain ? `@${selectedDomain}` : "@domain"
                      }
                      className="username-input-group"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please input email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      disabled
                      placeholder="Email will be generated automatically"
                      className="disabled-input-bg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            // External provider fields
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Please input email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="Enter complete email address" />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Card>

        {/* User Details Card */}
        <Card
          title="User Details"
          className="user-details-card"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Password (Optional)"
                name="password"
                rules={[
                  { min: 8, message: "Password must be at least 8 characters" },
                ]}
                extra="Leave empty to keep current password"
              >
                <Input.Password placeholder="Enter new password (leave empty to keep current)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Please input name" }]}
              >
                <Input placeholder="User's full name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select status" }]}
                className="status-select"
              >
                <Select>
                  <Option value="active" className="status-active-option">
                    Active
                  </Option>
                  <Option value="suspended" className="status-suspended-option">
                    Suspended
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Is Admin"
                name="isAdmin"
                className="admin-select"
              >
                <Select>
                  <Option value={true} className="admin-yes-option">
                    Yes
                  </Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Quota Limit (MB)"
                name="quotaLimit"
                rules={[
                  { required: true, message: "Please input quota limit" },
                  {
                    type: "number",
                    min: 1,
                    message: "Quota must be at least 1 MB",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter quota limit in MB"
                  min={1}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div className="form-actions">
          <Button
            type="default"
            onClick={() => history.goBack()}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ minWidth: "120px" }}
          >
            {loading && <LoadingOutlined className="submit-loading" />}
            Update User
          </Button>
        </div>
      </Form>

      {/* Success Notification Modal */}
      <EmailNotificationModal
        isOpen={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        type="success"
        title="Success"
        message="Email user updated successfully"
        autoCloseTime={3000}
      />

      {/* Error Notification Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Error"
        message={errorMessage}
      />

      {/* Warning Notification Modal */}
      <EmailNotificationModal
        isOpen={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        type="warning"
        title="Warning"
        message={warningMessage}
      />

      {/* Info Notification Modal */}
      <EmailNotificationModal
        isOpen={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        type="info"
        title="Information"
        message={infoMessage}
      />
    </div>
  );
};

export default UserEmailUpdateForm;
